import React, { useMemo, useReducer, useCallback } from "react"
import { useMutation, useQuery, useLazyQuery, MutationResult, ApolloError } from "@apollo/client"
import { FileNameResponse, AudioFormat, Language, AudioFile } from "../../../typings/types"
import { CHECK_FILE_NAME } from "../../queries/CHECK_FILE_NAME"
import CREATE_AUDIO from "../../mutations/CREATE_AUDIO"
import TRANSLATE_TEXT from "../../mutations/TRANSLATE_TEXT"
import { useResettableMutation } from "../../common/hooks/useResettableMutation"
import { TransactionMode } from "../../common/enums"
import { GET_PROMPT_CREATION_INFO } from "../../queries/GET_PROMPT_CREATION_INFO"
import {
    Action,
    updateText,
    updateAvailableLanguages,
    UpdateAvailableAudioFormats,
    updateSelect,
    ManualPageError,
    ManualPageState,
    getInitialState,
    reducer,
    Fields,
    error,
    clearError,
    reset
} from "./reducer"
import { useNotification } from "../../common/hooks/useNotification"
import { CanStoreVoiceFields } from "../../common/hooks/useNeuralVoiceCheck"

export interface ManualPageContextProps extends CanStoreVoiceFields {
    checkForFileNameError: () => void
    handleTranslate: () => void
    sourceAudio?: MutationResult<{ createAudio: AudioFile }>
    targetAudio?: MutationResult<{ createAudio: AudioFile }>
    handleCreateAudio: () => void
    translateTextLoading: boolean
    translationData: { translateText: { text: string } } | null | undefined
    translationError: ApolloError | undefined
    pageLoading: boolean
    state: ManualPageState
    dispatch: (action: Action) => void
    resetManualPage: () => void
}
export const ManualPageContext = React.createContext<ManualPageContextProps>({
    checkForFileNameError: () => {},
    handleTranslate: () => {},
    handleCreateAudio: () => {},
    translateTextLoading: false,
    translationData: { translateText: { text: "" } },
    translationError: undefined,
    pageLoading: false,
    state: getInitialState(),
    dispatch: (action: Action) => {},
    resetManualPage: () => {}
})

interface ManualPageProviderProps {
    value?: ManualPageContextProps
    children: React.ReactNode
}
export const ManualPageProvider: React.FunctionComponent<ManualPageProviderProps> = props => {
    const { notifySuccess, notifyError } = useNotification()
    const [state, dispatch] = useReducer(reducer, getInitialState())
    const { refetch, loading: pageLoading } = useQuery<{
        generateFileName: FileNameResponse
        availableLanguages: Language[]
        getAudioFormats: AudioFormat[]
    }>(GET_PROMPT_CREATION_INFO, {
        onCompleted: data => {
            dispatch(updateText(Fields.AudioFileName, data.generateFileName.fileName))
            if (data.getAudioFormats.length) {
                dispatch(UpdateAvailableAudioFormats(data.getAudioFormats))
                dispatch(updateSelect(Fields.FileFormat, data.getAudioFormats[0].id.toString()))
            }
            if (data.availableLanguages.length) {
                dispatch(updateAvailableLanguages(data.availableLanguages))
            }
        },
        onError: err => {
            dispatch(error(Fields.AudioFileName, ManualPageError.FILE_NAME_EXISTS))
            dispatch(error(Fields.OriginalText, ManualPageError.UNABLE_TO_FETCH_LANGUAGES))
        }
    })

    const [createSourceAudio, sourceAudio] = useResettableMutation<{ createAudio: AudioFile }>(
        CREATE_AUDIO,
        {
            onError: error => notifyError(`error generating source audio: ${error.message}`),
            onCompleted: () =>
                notifySuccess(
                    `${
                        state.languageOptions.source.find(o => o.id === state.sourceLanguage)?.name
                    } audio successfully generated`
                )
        }
    )

    const [createTargetAudio, targetAudio] = useResettableMutation<{
        createAudio: AudioFile
    }>(CREATE_AUDIO, {
        onError: error => notifyError(`error generating target audio: ${error.message}`),
        onCompleted: () =>
            notifySuccess(
                `${
                    state.languageOptions.target.find(o => o.id === state.targetLanguage)?.name
                } audio successfully generated`
            )
    })

    const handleCreateAudio = useCallback(() => {
        const fileName = state.fields.audioFileName.value

        if (state.fields.originalText.value !== "") {
            createSourceAudio({
                variables: {
                    audioInput: {
                        referenceId: state.referenceId,
                        mode: TransactionMode.SINGLE,
                        formatId: parseInt(state.fields.fileFormat.value),
                        voiceId: parseInt(state.fields.sourceVoice.value),
                        languageId: state.sourceLanguage,
                        fileName: fileName,
                        givenText: state.fields.originalText.value
                    }
                }
            })
        }
        if (state.fields.translatedText.value !== "") {
            createTargetAudio({
                variables: {
                    audioInput: {
                        referenceId: state.referenceId,
                        mode: TransactionMode.SINGLE,
                        formatId: parseInt(state.fields.fileFormat.value),
                        voiceId: parseInt(state.fields.targetVoice.value),
                        languageId: state.targetLanguage,
                        fileName: fileName,
                        givenText: state.fields.translatedText.value
                    }
                }
            })
        }
    }, [
        createSourceAudio,
        createTargetAudio,
        state.fields,
        state.sourceLanguage,
        state.targetLanguage
    ])

    const resetManualPage = useCallback(() => {
        dispatch(reset())
        refetch().then(({ data, error: err }) => {
            if (data) {
                dispatch(updateText(Fields.AudioFileName, data!.generateFileName.fileName))
                dispatch(updateAvailableLanguages(data.availableLanguages))
                dispatch(UpdateAvailableAudioFormats(data.getAudioFormats))
                dispatch(updateSelect(Fields.FileFormat, data.getAudioFormats[0].id.toString()))
            }
            if (err) {
                dispatch(error(Fields.AudioFileName, ManualPageError.FILE_NAME_EXISTS))
                dispatch(error(Fields.OriginalText, ManualPageError.UNABLE_TO_FETCH_LANGUAGES))
            }
        })
        sourceAudio.reset()
        targetAudio.reset()
    }, [refetch, sourceAudio, targetAudio])

    const [checkFileName] = useLazyQuery<{ checkFileName: FileNameResponse }>(CHECK_FILE_NAME, {
        onCompleted: () => {
            dispatch(clearError(Fields.AudioFileName))
        },
        onError: err => {
            dispatch(error(Fields.AudioFileName, "Filename already exists."))
        },
        fetchPolicy: "network-only"
    })

    const checkForFileNameError = useCallback(() => {
        if (!state.fields.audioFileName.value) {
            dispatch(error(Fields.AudioFileName, "Filename cannot be empty"))
        } else {
            checkFileName({
                variables: {
                    checkFileNameInput: {
                        fileName: state.fields.audioFileName.value
                    }
                }
            })
        }
    }, [state, checkFileName])

    const [
        translateText,
        { loading: translateTextLoading, error: translationError, data: translationData }
    ] = useMutation<{
        translateText: { text: string }
    }>(TRANSLATE_TEXT, {
        onCompleted: data => {
            dispatch(updateText(Fields.TranslatedText, data.translateText.text))
            notifySuccess("Translated text successfully")
        },
        onError: err => {
            dispatch(error(Fields.TranslatedText, ManualPageError.TRANSLATION_ERROR))
            notifyError(`Error translating text: ${err.message}`)
        }
    })

    const handleTranslate = useCallback(() => {
        if (state.fields.originalText.value === "") {
            dispatch(error(Fields.OriginalText, ManualPageError.EMPTY_INPUT))
        } else {
            dispatch(clearError(Fields.OriginalText))
            translateText({
                variables: {
                    translateTextInput: {
                        referenceId: state.referenceId,
                        mode: TransactionMode.SINGLE,
                        text: state.fields.originalText.value,
                        sourceLanguage: state.allLanguages[state.sourceLanguage].code,
                        targetLanguage: state.allLanguages[state.targetLanguage].code
                    }
                }
            })
        }
    }, [
        state.allLanguages,
        state.fields.originalText,
        state.sourceLanguage,
        state.targetLanguage,
        state.referenceId,
        translateText
    ])

    const manualPageContextValue = useMemo(
        () => ({
            checkForFileNameError,
            handleTranslate,
            handleCreateAudio,
            sourceAudio,
            targetAudio,
            translateTextLoading,
            translationData,
            translationError,
            state,
            pageLoading,
            dispatch,
            resetManualPage
        }),
        [
            checkForFileNameError,
            handleTranslate,
            handleCreateAudio,
            sourceAudio,
            targetAudio,
            translateTextLoading,
            translationData,
            translationError,
            pageLoading,
            state,
            dispatch,
            resetManualPage
        ]
    )
    return <ManualPageContext.Provider value={props.value ?? manualPageContextValue} {...props} />
}

export const useManualPageContext = (): ManualPageContextProps => {
    const context = React.useContext(ManualPageContext)
    if (!context) throw new Error("useManualPageContext must be used within an ManualPageProvider")
    return context
}
